import { isBareWorkspace } from './brandWorkspace.js';

/* ============================================================
   Dathent — finishSetup.js (TASK-214, issue #332)
   The pure half of the dashboard "Finish setup" card. Owner decision
   2026-09-02: with the preview paywall on (TASK-143) onboarding is
   optional, so a user who skipped or abandoned it lands on the dashboard
   with no brand profile — and nothing on that dashboard told them what
   was missing. The card shows a short progress (brand / content plan /
   social accounts) and a CTA back to /onboarding, ONLY while the brand
   profile is missing, and disappears the moment setup is complete. A stale
   in_progress session never overrides an existing brand profile.

   No React, no network: DashboardDataContext feeds readSetupStatus() the
   three Promise.allSettled answers it already collects, Dashboard.jsx
   renders whatever finishSetupState() returns (null = no card).

   Fail QUIET, never loud: the brand-profile probe mirrors RequireOnboarding
   (App.jsx) and OnboardingNudge — a clean 404 is the only "no brand"
   signal; any other error is "unknown" and hides the card rather than
   nagging a fully set-up user over a network blip.
   ============================================================ */

function unwrap(res) {
  return res?.data ?? res;
}

// TASK (2026-09-11, bare-brand-prompts #2): the FinishSetupCard's own "×"
// dismiss exists ONLY for the bare-workspace case (never for a genuinely
// missing brand, criterion 2) — a per-account localStorage marker
// (lib/finishSetupDismiss.js writes/reads it) hides the card for this long,
// then it reappears. Kept here, pure and testable: the marker's age is the
// only thing that decides show/hide; reading/writing localStorage itself
// lives outside this file.
export const BARE_WORKSPACE_HIDE_MS = 7 * 24 * 60 * 60 * 1000;

export function isBareWorkspaceHidden(hiddenAt, now = Date.now()) {
  if (!hiddenAt) return false;
  const ts = typeof hiddenAt === 'string' ? Date.parse(hiddenAt) : Number(hiddenAt);
  if (!Number.isFinite(ts)) return false;
  return now - ts < BARE_WORKSPACE_HIDE_MS;
}

/**
 * Map the allSettled answers of
 *   [api.profile.getBrandProfile(), api.onboarding.getStatus(), api.strategy.get()]
 * to a flat status object. Missing entries count as failed probes.
 *
 * @returns {{ brand: 'present'|'missing'|'unknown', onboarding: string, hasPlan: boolean, planUpdatedAt: string|null }}
 *   `onboarding` is the server session status ('in_progress', …), 'none' when
 *   there is no active session, 'unknown' when the probe failed.
 */
export function readSetupStatus({ brand, session, strategy } = {}) {
  let brandState = 'unknown';
  let bareWorkspace = false;
  // TASK-327 fix (judge critic, item 8): the raw brand-profile payload,
  // additive to the existing coarse `brand: 'present'/'missing'/'unknown'`
  // status — lets a consumer like Plans.jsx compute missingBrandFields()
  // from the SAME probe DashboardDataContext already ran for setupStatus,
  // instead of firing its own separate brand-profile fetch.
  let profile = null;
  if (brand?.status === 'fulfilled') {
    brandState = 'present';
    const payload = unwrap(brand.value);
    profile = payload?.brandProfile || payload || null;
    bareWorkspace = isBareWorkspace(profile);
  }
  else if (brand?.status === 'rejected' && brand.reason?.status === 404) brandState = 'missing';

  let onboarding = 'unknown';
  if (session?.status === 'fulfilled') {
    onboarding = unwrap(session.value)?.onboardingSession?.status || 'none';
  }

  const strategyPayload = strategy?.status === 'fulfilled' ? unwrap(strategy.value) : null;
  const hasPlan = Boolean(strategyPayload?.strategy);
  const planUpdatedAt = strategyPayload?.updatedAt || strategyPayload?.strategy?.updatedAt || null;

  return { brand: brandState, bareWorkspace, profile, onboarding, hasPlan, planUpdatedAt };
}

/**
 * Decide whether the card shows and what it says.
 *
 * @param {object} opts
 * @param {ReturnType<typeof readSetupStatus>|null} opts.setup  null until fetched
 * @param {Array} [opts.socialAccounts]   DashboardDataContext.socialAccounts
 * @param {boolean} [opts.justCompleted]  onboarding finished within the last
 *        minute (lib/onboardingCompletion.js) — the fresh brand profile may
 *        not have reached a D1 read replica yet, so a 404 right now is lag,
 *        not "unfinished"; RequireOnboarding uses the same grace window.
 * @returns {null | { resume: boolean, steps: Array<{key:string, done:boolean, count?:number}>, done: number, total: number }}
 */
export function isPostingPlan(entitlements) {
  return (entitlements?.plan || entitlements?.planKey || entitlements?.effectivePlan) === 'posting';
}

export function finishSetupState({ setup, socialAccounts = [], postCount = 0, justCompleted = false, entitlements = null, firstPostId = null, chatgptOrigin = false, bareWorkspaceHiddenAt = null, now = Date.now() }) {
  if (!setup || justCompleted) return null;
  const posting = isPostingPlan(entitlements);
  const connected = Array.isArray(socialAccounts) ? socialAccounts.length : 0;

  if (posting && setup.brand === 'present') {
    // Variant A (owner decision 2026-09-09, dashboard setup card redesign):
    // `steps` holds ONLY the two required rows now — prepareBrand moved out
    // to its own `optional` field so it can render as a quiet footer note
    // instead of a fake third step that never counted toward done/total
    // anyway. `activeKey` is the first not-done required step, computed
    // here (not in the component) so FinishSetupCard stays a pure render of
    // whatever this function returns.
    const steps = [
      { key: 'social', done: connected > 0, count: connected },
      // firstPostId travels on the step itself (rather than only at the top
      // level) so FinishSetupCard can render the "Open post" link right next
      // to this specific row without threading a second prop through it.
      { key: 'firstPost', done: Number(postCount) > 0, firstPostId: firstPostId || null },
    ];
    // TASK-319: a posting brand is auto-created bare by requireOnboarding.js
    // (createMinimalBrand) — bareWorkspace is therefore permanently true for
    // a typical Scheduler user, and the old `!setup.bareWorkspace && …` guard
    // meant this card could never disappear once social+firstPost were done.
    // The optional prepareBrand field (below) still shows WHILE the card is
    // otherwise visible; it no longer keeps the card alive by itself.
    if (steps.every((step) => step.done)) return null;
    return {
      resume: false,
      posting: true,
      steps,
      optional: setup.bareWorkspace ? { key: 'prepareBrand' } : null,
      activeKey: steps.find((step) => !step.done)?.key || null,
      done: steps.filter((step) => step.done).length,
      total: steps.length,
    };
  }

  const noBrand = setup.brand === 'missing';
  // TASK (2026-09-11, bare-brand-prompts #1): a brand that EXISTS but is
  // still the bare/minimal placeholder (isBareWorkspace) is "below the
  // strategy minimum" (EPIC-029 D6) and gets the same reminder as no brand
  // at all — on ANY plan now (owner finding 2026-09-10: a Scheduler account
  // that upgrades to Starter keeps its auto-created bare workspace and saw
  // NO prompt to finish it). Previously gated on chatgptOrigin (TASK B4,
  // 2026-09-10) because a bare workspace can ALSO come from the existing
  // "Post as yourself" quick-brand action (QuickBrandSheet, offered on this
  // very card) — that gate is removed; the dismiss control below (criterion
  // 2) is what lets a deliberate "post as yourself" account quiet the card
  // instead. chatgptOrigin is kept as a parameter (call sites still pass it)
  // but no longer affects this branch.
  const bareBrand = setup.brand === 'present' && setup.bareWorkspace === true;
  if (!noBrand && !bareBrand) return null;
  // The bare case is dismissable (criterion 2); a genuinely missing brand
  // (noBrand) never reads the marker, so it can never be hidden this way.
  if (bareBrand && isBareWorkspaceHidden(bareWorkspaceHiddenAt, now)) return null;
  const resume = setup.onboarding === 'in_progress';

  const steps = [
    { key: 'brand', done: setup.brand === 'present' && !bareBrand },
    ...(!posting ? [{ key: 'plan', done: setup.hasPlan === true }] : []),
    { key: 'social', done: connected > 0, count: connected },
  ];
  return {
    resume,
    posting,
    bareWorkspace: bareBrand,
    steps,
    done: steps.filter((s) => s.done).length,
    total: steps.length,
  };
}
